import React, { useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';

import { useUserContext } from '../contexts/UserContext';
import { UEventFormField, useEditEventContext } from '../contexts/EditEventContext';
import { BasePageContent } from './BasePage';
import { startUserEvent, UserEventType } from '../lib/performance';
import { useBackgroundContext } from '../contexts/BackgroundContext';
import { useEventCacheContext } from '../contexts/EventCacheContext';
import { hasRsvpedUsers, isUserHost } from '../lib/attendance';
import { useModalContext } from '../contexts/ModalContext';
import { editEventDetails } from '../api/ElkEventService';
import EventForm from '../components/forms/EventForm';
import { addLocationToEvent } from '../lib/location';
import { UButterBarLevel, useButterBarContext } from '../contexts/ButterBarContext';
import SendUpdateNotificationModal from '../components/modals/SendUpdateNotificationModal';
import { IUEventErrors } from '../lib/event';
import FooterLinks from '../components/FooterLinks';
import BasePageHeader, { UPageType } from './BasePageHeader';
import { isInvalidEventDuration } from '../lib/time';
import PageContentWithActionMenu from '../components/EventForm/PageContentWithActionMenu';
import { ActionMenuTab } from '../components/EventForm/ActionMenu';

import { logSumoEvent, stringifyError, ULogApplication, ULogSeverity, ULogTag } from 'Common/src/api/SumoLogicApi';

import { TAppElkPhotoUploadMode } from 'TProtocol/prototypes/events/messages';
import { logEvent } from 'firebase/analytics';
import { firebaseAnalytics } from '../core/libs/Firebase';
import EventPage from './Event/EventPage';
import Modal from '../components/modals/Modal';
import { ActionRow, StickyFooter, StickyFooterWhiteButton } from '../components/EventForm/Common';
import { WhiteButton } from '../components/buttons/WhiteButton';

const EditEventPage = () => {
  const userContext = useUserContext();
  const editEventContext = useEditEventContext();
  const eventCacheContext = useEventCacheContext();
  const backgroundContext = useBackgroundContext();
  const modalContext = useModalContext();
  const butterBarContext = useButterBarContext();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const pageContentRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const [confirmedSendUpdate, setConfirmedSendUpdate] = useState<boolean | undefined>();
  const [originalLocation, setOriginalLocation] = useState<string | undefined>();
  const [openTab, setOpenTab] = useState<ActionMenuTab | undefined>(undefined);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const disableBlockerRef = useRef<boolean>(false);

  const event = editEventContext.event;

  const blocker = useBlocker(() => {
    return !disableBlockerRef.current && editEventContext.hasEventChanged();
  });

  useEffect(() => {
    if (editEventContext.event?.id === undefined && eventId === undefined) {
      navigate('/');
    } else if (editEventContext.event?.id !== eventId && eventId !== undefined) {
      void fetchEvent(eventId);
    }
  }, [editEventContext.event?.id, eventId]);

  useEffect(() => {
    editEventContext.setHostUploadMode(
      event?.albumDetails?.photoUploadMode ?? userContext.defaultUploadMode ?? TAppElkPhotoUploadMode.AUTOMATIC);
  }, [eventId, event?.albumDetails?.photoUploadMode]);

  useEffect(() => {
    backgroundContext.setColors(
      {
        colors: event?.colors,
        animationUrl: event?.backgroundAnimationUrl,
        animationDisabled: event?.videoBackgroundDisabled
      }
    );
  }, [event?.colors, event?.animation, event?.backgroundAnimationUrl, event?.videoBackgroundDisabled]);

  useEffect(() => {
    document.title = event?.title ? `Shine Parties - Edit - ${event.title}` : 'Shine - Edit Party';
    logEvent(firebaseAnalytics, 'page_view', {
      page_title: 'Edit Event - Analytics'
    });
  }, [event?.title]);

  useEffect(() => {
    if (confirmedSendUpdate !== undefined) {
      modalContext.hide();
      void saveEvent(blocker.state === 'blocked');
    }
  }, [confirmedSendUpdate]);

  useEffect(() => {
    if (originalLocation === undefined && event?.location.displayName) {
      setOriginalLocation(event?.location.displayName);
    }
  }, [event?.location.displayName]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      modalContext.show({
        contents: <Modal
          title={'Save changes to your party?'}
          primaryButtonLabel={'Yes'} secondaryButtonLabel={'No'}
          onPrimaryClick={blockerModalSave}
          onSecondaryClick={blockerModalCancel}
          onClickOutside={blocker.reset}
          useSystemColors={true}/>
      })
    }
  }, [blocker.state]);

  const logUserAction = (action: string) => {
    void logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.DEBUG,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: `[EditEventPage] ${action}`
    });
  };

  const fetchEvent = async (eventId: string) => {
    try {
      const event = await editEventContext.fetchEvent(eventId);

      if (event === undefined || !event.isHost) {
        navigate(`/event/${eventId}`);
      }
    } catch (e) {
      void logSumoEvent({
        app: ULogApplication.ELK,
        severity: ULogSeverity.WARN,
        userId: userContext.id,
        tag: ULogTag.Network,
        message: `[EditEventPage] fetchEvent - Error: ${stringifyError(e)}`
      });
      butterBarContext.show({
        level: UButterBarLevel.ERROR,
        contents: `Something went wrong fetching your party: ${stringifyError(e)}`
      });
    }
  };

  // If there is no event, i.e. it is loading, then set the color to white, so it can fade into the color when it's
  // fetched.  This should only happen if the user directly links to this page -- navigating from the list of events
  // page should have set the event in the context.
  // useEffect(() => {
  //   setColor(eventContext?.event?.colors.primary?? '#ffffff');
  // }, [eventContext?.event?.colors.primary);

  const onBackClick = (calledFromBlocker = false) => {
    editEventContext.clearEvent();

    logUserAction('Back button click');

    if (calledFromBlocker) {
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    } else {
      if (event !== undefined) {
        navigate(`/event/${eventId}`);
      } else {
        navigate('/');
      }
    }
  };

  const saveEvent = async (calledFromBlocker = false) => {
    startUserEvent(UserEventType.SaveEvent);

    logUserAction(`Save event${calledFromBlocker ? ' (from blocker)' : ''}`);

    const event = await editEventContext.generateEvent();
    const editEvent = editEventContext.event;
    if (event !== undefined) {
      if (hasRsvpedUsers(editEvent) && confirmedSendUpdate === undefined) {
        modalContext.show({
          contents: <SendUpdateNotificationModal close={modalContext.hide} confirm={setConfirmedSendUpdate}/>
        });
        return;
      }

      if (editEvent !== undefined) {
        // Should always be true, but let's be sure?
        editEvent.isHost = isUserHost(editEvent, userContext.id);
        eventCacheContext.setEvent(editEvent);
      }
      try {
        await editEventDetails(userContext, event, editEvent?.hostUploadMode, !confirmedSendUpdate, true);

        setConfirmedSendUpdate(undefined);
        editEventContext.clearEvent();
        if (calledFromBlocker) {
          if (blocker.state === 'blocked') {
            blocker.proceed();
          }
        } else {
          navigate('/event/' + event?.id);
        }
      } catch (e) {
        void logSumoEvent({
          app: ULogApplication.ELK,
          severity: ULogSeverity.ERROR,
          userId: userContext.id,
          tag: ULogTag.Network,
          message: `[EditEventPage] Error during event save: ${stringifyError(e)}`
        });
        butterBarContext.show({
          level: UButterBarLevel.ERROR,
          contents: 'Sorry, something went wrong saving your party. Please try again. If the problem persists, please contact us at support@sunshine.com.',
          duration: 20000
        });
      }
    }
  };

  const handleSave = async (calledFromBlocker = false) => {
    if (event?.location.displayName === originalLocation) {
      // Location was not edited, need to fetch address info passively
      await addLocationToEvent(event);
    }

    editEventContext.clearFieldValidity();
    const validForm = validateForm();
    const validHtmlForm = formRef.current?.reportValidity();
    if (!validForm || !validHtmlForm) {
      editEventContext.scrollToError();
      return;
    }

    await saveEvent(calledFromBlocker);
  };

  const validateForm = (): boolean => {
    if (!event) {
      return false;
    }

    const formErrors: IUEventErrors = {
      invalidTitle: false,
      invalidStartDate: false,
      invalidDatePair: false,
      invalidDuration: false,
      missingImage: false
    };
    let valid = true;

    if (event.title.trim() === '') {
      editEventContext.setFieldInvalid(UEventFormField.Title);
      formErrors.invalidTitle = true;
      valid = false;
    }
    if (event.photoUrl === undefined || event.photoUrl === '') {
      editEventContext.setFieldInvalid(UEventFormField.Image);
      formErrors.missingImage = true;
      valid = false;
    }
    if (event.startTime < Date.now()) {
      editEventContext.setFieldInvalid(UEventFormField.Time);
      formErrors.invalidStartDate = true;
      valid = false;
    }
    if (event.endTime && event.startTime > event.endTime) {
      editEventContext.setFieldInvalid(UEventFormField.Time);
      formErrors.invalidDatePair = true;
      valid = false;
    }
    if (event.endTime && isInvalidEventDuration(event.startTime, event.endTime)) {
      editEventContext.setFieldInvalid(UEventFormField.Time);
      formErrors.invalidDuration = true;
      valid = false;
    }
    if (event.location.address === undefined) {
      editEventContext.setFieldInvalid(UEventFormField.Location);
      formErrors.invalidLocation = true;
      valid = false;
    }

    editEventContext.updateFormErrors(formErrors);

    return valid;
  };

  const scrollToTop = () => {
    pageContentRef.current?.scrollTo(0, 0);
  };

  const blockerModalSave = () => {
    modalContext.hide();
    void handleSave(true);
  };

  const blockerModalCancel = () => {
    modalContext.hide();
    onBackClick(true);
  };

  const formSave = () => {
    disableBlockerRef.current = true;
    void handleSave(false);
  }

  const formCancel = () => {
    disableBlockerRef.current = true;
    onBackClick(false);
  }

  const handlePreviewClick = () => {
    logSumoEvent({
      app: ULogApplication.ELK,
      severity: ULogSeverity.INFO,
      userId: userContext.id,
      tag: ULogTag.UserAction,
      message: '[EditEventPage] Clicked on preview'
    });

    setShowPreview(true);
  }

  if (showPreview) {
    return <EventPage readOnly={true} showPreview={true} setShowPreview={setShowPreview}/>
  } else {

    return <>
      <PageContentWithActionMenu event={event} openTab={openTab} setOpenTab={setOpenTab} scrollToTop={scrollToTop}>
        <BasePageHeader page={UPageType.EditEvent} hide={openTab !== undefined}/>
        <BasePageContent $wide={true} $full={true} $relative={false} $flush={true} containerRef={pageContentRef}>
          {event ?
            <EventForm event={event} formRef={formRef} setOpenTab={setOpenTab}/> : null}
          <FooterLinks/>
        </BasePageContent>
        <StickyFooter>
          <ActionRow $noMargins={true}>
            <StickyFooterWhiteButton $invert={true} type="button" onClick={formSave}>
              Save
            </StickyFooterWhiteButton>
            <StickyFooterWhiteButton $invert={true} type="button" onClick={handlePreviewClick}>
              Preview
            </StickyFooterWhiteButton>
            <StickyFooterWhiteButton $invert={true} type="button" onClick={formCancel}>
              Cancel
            </StickyFooterWhiteButton>
          </ActionRow>
        </StickyFooter>
      </PageContentWithActionMenu>
    </>;
  }
};

export default EditEventPage;


